import type { FilterConfigModalItem } from '@rocco/ui/filter-sort/types/filter-config-modal';
import {
    type FilterState,
    isLocationFilterState,
    isMultipleValuesFilterState,
    isRangeFilterState,
    isSearchableListFilterState,
    isSingleValueFilterState,
} from '@rocco/ui/filter-sort/view-models/filter-state/filter-state';
import type { FilterStatesKey } from '@rocco/ui/filter-sort/view-models/filter-states';
import type { AggregationArgs } from '@rocco/ui/filter-sort/hooks/use-search-filters';
import { UIConfigurationError } from '@rocco/exceptions/ui-configuration-error';
import {
    FiltersModalColors,
    FiltersModalMultipleValues,
    FiltersModalMultipleValuesButtons,
    FiltersModalPrice,
    FiltersModalSearchableList,
    FiltersModalSimpleRange,
    FiltersModalSingleValue,
} from '@rocco/ui/filter-sort/components/filters-modal/elements';
import type { DisplayMode } from '@rocco/types/enum/display-mode';
import { FiltersModalLocation } from '@rocco/ui/filter-sort/components/filters-modal/elements/FiltersModalLocation';

export interface FilterModalElementProps<T, V> {
    filterConfigItem: T;
    filterState: V;
    onFilterStateUpdate: (state: V, shouldAggregate?: boolean) => void;
    displayMode: DisplayMode;
}

export const FiltersModalElement = ({
    filterConfigItem,
    filterState,
    onUpdateFilterState,
    onAlphabeticalGrouping,
    displayMode,
    hideSelectedListChips = false,
}: {
    filterConfigItem: FilterConfigModalItem;
    filterState: FilterState | null;
    onUpdateFilterState: (
        filterKey: FilterStatesKey,
        newFilterState: FilterState,
        shouldAggregate?: boolean,
    ) => void;
    onAlphabeticalGrouping: (args: AggregationArgs) => void;
    displayMode: DisplayMode;
    /**
     * Searchable lists only: suppress their selected-item chips when the
     * host already surfaces selections (the refine sheet's chips row).
     */
    hideSelectedListChips?: boolean;
}) => {
    if (filterState === null) {
        throw new UIConfigurationError(
            `Filter has a null state: ${filterConfigItem.filterKey}`,
        );
    }

    const handleUpdateState = (
        newState: FilterState,
        shouldAggregate?: boolean,
    ) => {
        onUpdateFilterState(
            filterConfigItem.filterKey,
            newState,
            shouldAggregate,
        );
    };

    switch (filterConfigItem.type) {
        case 'default':
            if (isSingleValueFilterState(filterState)) {
                return (
                    <FiltersModalSingleValue
                        filterConfigItem={filterConfigItem}
                        filterState={filterState}
                        onFilterStateUpdate={handleUpdateState}
                        displayMode={displayMode}
                    />
                );
            }
            if (isMultipleValuesFilterState(filterState)) {
                return (
                    <FiltersModalMultipleValuesButtons
                        filterConfigItem={filterConfigItem}
                        filterState={filterState}
                        onFilterStateUpdate={handleUpdateState}
                        displayMode={displayMode}
                    />
                );
            }
            if (isLocationFilterState(filterState)) {
                return (
                    <FiltersModalLocation
                        filterConfigItem={filterConfigItem}
                        filterState={filterState}
                        onFilterStateUpdate={handleUpdateState}
                        displayMode={displayMode}
                    />
                );
            }
            break;
        case 'multiple':
            if (isMultipleValuesFilterState(filterState)) {
                return (
                    <FiltersModalMultipleValues
                        filterConfigItem={filterConfigItem}
                        filterState={filterState}
                        onFilterStateUpdate={handleUpdateState}
                        displayMode={displayMode}
                    />
                );
            }
            break;
        case 'color':
            if (isMultipleValuesFilterState(filterState)) {
                return (
                    <FiltersModalColors
                        filterConfigItem={filterConfigItem}
                        filterState={filterState}
                        onFilterStateUpdate={handleUpdateState}
                        displayMode={displayMode}
                    />
                );
            }
            break;
        case 'searchable':
            if (isSearchableListFilterState(filterState)) {
                return (
                    <FiltersModalSearchableList
                        filterConfigItem={filterConfigItem}
                        filterState={filterState}
                        onFilterStateUpdate={handleUpdateState}
                        onAlphabeticalGrouping={onAlphabeticalGrouping}
                        displayMode={displayMode}
                        hideSelectedChips={hideSelectedListChips}
                    />
                );
            }
            break;
        case 'price':
            if (isRangeFilterState(filterState)) {
                return (
                    <FiltersModalPrice
                        filterConfigItem={filterConfigItem}
                        filterState={filterState}
                        onFilterStateUpdate={handleUpdateState}
                        displayMode={displayMode}
                    />
                );
            }
            break;
        case 'simpleRange':
            if (isRangeFilterState(filterState)) {
                return (
                    <FiltersModalSimpleRange
                        filterConfigItem={filterConfigItem}
                        filterState={filterState}
                        onFilterStateUpdate={handleUpdateState}
                        displayMode={displayMode}
                    />
                );
            }
            break;
    }

    return null;
};
